import { useMemo } from 'react';
import { Node } from '../../Tree';
import { connectToPreferenceStore } from '../forms/connect';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { GeneralForm } from '../forms/global/GeneralForm';
import { GlobalSettingsKeys } from '../GlobalSettingsKeys';
import { RegionAndLanguageForm } from '../forms/global/RegionAndLanguageForm';
import { AccessibilityForm } from '../forms/global/AccessibilityForm';
import { FormGroup } from '../model/FormGroup';

export const useGetGlobalTreeNodeAndForms = (hasWriteAccess: boolean) => {
  const localize = useNCTextLocalizer('Settings');

  return useMemo(() => {
    const forms: Map<Node, FormGroup['form']> = new Map();

    const generalNode = {
      id: 'G-0-C-1',
      text: localize('settings.groups.global.subgroups.general.section.title'),
    };

    forms.set(generalNode, connectToPreferenceStore(GeneralForm, [GlobalSettingsKeys.THEMES_KEY]));

    const regionAndLanguageNode = {
      id: 'G-0-C-2',
      text: localize('settings.groups.global.subgroups.regionAndLanguage.section.title'),
    };

    if (hasWriteAccess) {
      forms.set(
        regionAndLanguageNode,
        connectToPreferenceStore(RegionAndLanguageForm, [
          GlobalSettingsKeys.FORMAT_LOCALE_KEY,
          GlobalSettingsKeys.OFFLINE_LOCALE_KEY,
        ])
      );
    }

    const accessibilityNode = {
      id: 'G-0-C-3',
      text: localize('settings.groups.global.subgroups.accessibility.section.title'),
    };

    forms.set(
      accessibilityNode,
      connectToPreferenceStore(AccessibilityForm, [
        GlobalSettingsKeys.PLAY_EARCONS,
        GlobalSettingsKeys.PLAY_VISUAL_EFFECTS,
        GlobalSettingsKeys.ENABLE_POPUP_DISPLAY_DURATION,
        GlobalSettingsKeys.POPUP_DISPLAY_DURATION,
        GlobalSettingsKeys.INVERT_APP_COLORS,
        GlobalSettingsKeys.ENABLE_KEYBOARD_TOOLTIPS,
        GlobalSettingsKeys.KEYBOARD_TOOLTIP_LOCATION,
        GlobalSettingsKeys.FOCUS_OVERRIDE,
        GlobalSettingsKeys.FOCUS_THICKNESS,
        GlobalSettingsKeys.FOCUS_COLOR,
        GlobalSettingsKeys.FOCUS_OPACITY,
      ])
    );

    const globalNode: Node = {
      id: 'G-0',
      text: localize('settings.groups.global.section.title'),
      children: hasWriteAccess
        ? [generalNode, regionAndLanguageNode, accessibilityNode]
        : [generalNode, accessibilityNode],
    };

    return { nodes: [globalNode], forms, branches: [globalNode] };
  }, [hasWriteAccess, localize]);
};
